/**
 * Metaship sign-in hand-off (plan M9, option A, decided by Jarryd Pearce 2026-11-01).
 *
 * A second build of this shell (`VITE_SIGNIN_HANDOFF=metaship`, base `/shell/`) is served on
 * metaship.ai at `/signin` (CloudFront router → `/shell/index.html` in the root app bucket).
 * It shows ONLY the split sign-in page (SignInSplit + SignInForm). It does not resolve a tenant
 * app, register a service worker, load modules or keep a session of its own.
 *
 * metaship.ai and this page are the same host, so the Better Auth session cookie that the form
 * sets is the legacy app's cookie too. After a successful password (or 2FA) sign-in the page
 * hands over to the legacy app at `/signin/return`, which turns the session into the app
 * session (Vuex, org, permissions: legacy-client utils/identitySession.js). Google, Microsoft,
 * magic-link and verification links already come back to `/signin/return`
 * (auth/identity.ts identityReturnUrl), and the reset link to `/reset-password`; on
 * metaship.ai both are legacy-client routes.
 *
 * Better Auth only: the mode is fixed to "identity", so the page has no Auth0 link.
 */
import { ref } from "vue";
import { defaultLoginPage, type ResolveTenantAppResponse } from "@/tenant/types";
import { IDENTITY_RETURN_PATH } from "@/auth/identity";

/** True in the metaship.ai hand-off build only. */
export function isMetashipHandoff(): boolean {
  return import.meta.env.VITE_SIGNIN_HANDOFF === "metaship";
}

/** The tenant the sign-in page draws: Metaship, no modules, no custom landing page. */
export const METASHIP_SIGNIN_TENANT: ResolveTenantAppResponse = {
  found: true,
  slug: "metaship",
  kind: "PRODUCT",
  name: "Metaship",
  status: "LIVE",
  homePath: null,
  homePage: null,
  organisation: null,
  // Metaship's own look (frontend style guide + landing page): the panel is the landing-page
  // navy, the accents the Metaship primary. Background/surface stay on the theme defaults so
  // the light theme still works. The logo tile is white, so it needs the BLACK cube (the
  // first-party /Metaship_logo.svg is white artwork); a blocked cdn.* falls back to the "M" tile.
  branding: {
    logoLightUrl: "https://cdn.metaship.ai/metaship/MetashipCube_Black_512.png",
    logoDarkUrl: "https://metaship.ai/Metaship_logo.svg",
    iconUrl: "https://cdn.metaship.ai/metaship/MetashipCube_Black_512.png",
    colours: { primary: "#649ECE", secondary: "#141E2C", tertiary: "#649ECE", background: null, surface: null, navbar: null },
    // The page follows the person's theme (resolveMetashipDark), not this default.
    defaultTheme: "dark",
    identity: "metaship",
  },
  modules: [],
  importMap: { imports: [] },
  // First-party terms page (cdn.* is blocked by browser extensions); privacy is section 11 of the same document.
  loginPage: defaultLoginPage({
    // The PDF itself (was the /terms page): the footer opens a .pdf in the shared @pdfviewer.
    termsUrl: "https://metaship.ai/terms_and_conditions.pdf",
    privacyUrl: "https://metaship.ai/terms_and_conditions.pdf#page=7",
    poweredBy: false,
    // The landing-page hero (test.metaship.ai): one record, every party, live.
    panelScene: "network",
  }),
  selfServeSignup: false,
};

/** Hand the signed-in Better Auth session to the legacy app. It reads the session from the cookie. */
export function handoffToApp(): void {
  window.location.assign(IDENTITY_RETURN_PATH);
}

/**
 * The legacy client's Auth0 start route on this origin (legacy-client Auth0Start.vue). A HELD
 * account (subject collision, Auth0 MFA) gets USE_PREVIOUS_SIGN_IN from Better Auth and must
 * still reach Auth0 while the host mode is "both"; a host set back to "auth0" goes there directly.
 */
export const PREVIOUS_SIGN_IN_PATH = "/auth0/start";

/** Leave the hand-off page for the Auth0 sign-in (mode "both" link, or mode "auth0" rollback). */
export function startPreviousSignIn(): void {
  window.location.assign(PREVIOUS_SIGN_IN_PATH);
}

/** The legacy client's "sign-out not confirmed" marker (legacy-client utils/identityClient.js). */
export const SIGN_OUT_PENDING_KEY = "metaship:identity:signOutPending";
const SETTLE_TIMEOUT_MS = 5000;

/**
 * True while a legacy sign-out could not be settled. Login.vue then shows an error and
 * "Try again" INSTEAD of the form: a new sign-in on top of it would be signed out again by the
 * legacy client at /signin/return (Codex review).
 */
export const signOutUnsettled = ref(false);

/**
 * Before the form is used: retry a legacy sign-out that the server did not confirm. Otherwise
 * the legacy client retries it at /signin/return and ends the NEW session (Codex review).
 * Clears the marker only on a confirmed sign-out. Resolves true when nothing is pending.
 */
export async function settlePendingSignOut(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  let pending = false;
  try {
    pending = window.localStorage.getItem(SIGN_OUT_PENDING_KEY) === "1";
  } catch {
    return true;
  }
  if (!pending) return true;
  try {
    const res = await fetchImpl(`${window.location.origin}/identity/sign-out`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: "{}",
      signal: AbortSignal.timeout(SETTLE_TIMEOUT_MS),
    });
    if (!res.ok) return false;
    window.localStorage.removeItem(SIGN_OUT_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}


/**
 * One theme across metaship.ai, /signin and /portal (2026-11-01). The same rule runs in the landing
 * page (website/index.html head script):
 *   1. the app's theme on this browser ("metaship-global-state".darkMode, else "vuex".darkMode),
 *   2. else the landing page's saved choice ("ms-scheme"),
 *   3. else the browser / OS preference.
 */
export function resolveMetashipDark(
  storage: Pick<Storage, "getItem"> = localStorage,
  prefersLight: () => boolean = () => window.matchMedia?.("(prefers-color-scheme: light)").matches ?? false,
): boolean {
  for (const key of ["metaship-global-state", "vuex"]) {
    try {
      const value = JSON.parse(storage.getItem(key) || "null") as { darkMode?: unknown } | null;
      if (typeof value?.darkMode === "boolean") return value.darkMode;
    } catch {
      // A broken entry: try the next source.
    }
  }
  const saved = storage.getItem("ms-scheme");
  if (saved === "light") return false;
  if (saved === "dark") return true;
  return !prefersLight();
}

/**
 * Save a theme choice where all three pages read it. Only the darkMode field of an EXISTING app entry
 * changes (the root app replaces its whole state from that JSON, so never create a partial one).
 */
export function saveMetashipDark(dark: boolean, storage: Pick<Storage, "getItem" | "setItem"> = localStorage): void {
  try {
    storage.setItem("ms-scheme", dark ? "dark" : "light");
    const global = JSON.parse(storage.getItem("metaship-global-state") || "null");
    if (global && typeof global === "object") {
      storage.setItem("metaship-global-state", JSON.stringify({ ...global, darkMode: dark, themeChosen: true }));
    }
    const vuex = JSON.parse(storage.getItem("vuex") || "null");
    if (vuex && typeof vuex === "object") storage.setItem("vuex", JSON.stringify({ ...vuex, darkMode: dark }));
  } catch {
    // Storage blocked or full: the choice lasts for this page only.
  }
}
